import React, { useState, useRef, useEffect } from 'react';
import { Trophy, RotateCcw } from 'lucide-react';

// pos: -100 (you win) .. +100 (bot wins). Start at 0.
const WIN = 100;

export default function TugOfWar() {
  const [phase, setPhase] = useState('idle'); // idle | playing | done
  const [pos, setPos] = useState(0);
  const [result, setResult] = useState(null);
  const [wins, setWins] = useState(() => { try { return Number(localStorage.getItem('spc_tug_wins')) || 0; } catch { return 0; } });

  const posRef = useRef(0);
  const startRef = useRef(0);
  const rafRef = useRef(null);
  const phaseRef = useRef('idle');

  const setPhaseBoth = (p) => { phaseRef.current = p; setPhase(p); };
  const stop = () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); rafRef.current = null; };
  useEffect(() => () => stop(), []);

  const finish = (res) => {
    stop();
    setPhaseBoth('done');
    setResult(res);
    if (res === 'win') setWins(prev => { const n = prev + 1; try { localStorage.setItem('spc_tug_wins', String(n)); } catch {} return n; });
  };

  const loop = () => {
    if (phaseRef.current !== 'playing') return;
    const elapsed = (performance.now() - startRef.current) / 1000;
    // Bot pulls harder the longer the match runs (tension ramps up)
    const botPull = 0.35 + Math.min(0.55, elapsed * 0.06);
    posRef.current = Math.max(-WIN, Math.min(WIN, posRef.current + botPull));
    setPos(posRef.current);
    if (posRef.current >= WIN) { finish('lose'); return; }
    rafRef.current = requestAnimationFrame(loop);
  };

  const begin = () => {
    posRef.current = 0; setPos(0); setResult(null);
    startRef.current = performance.now();
    setPhaseBoth('playing');
    rafRef.current = requestAnimationFrame(loop);
  };

  const pull = () => {
    if (phaseRef.current !== 'playing') return;
    posRef.current = Math.max(-WIN, posRef.current - 5);
    setPos(posRef.current);
    if (posRef.current <= -WIN) finish('win');
  };

  const markerPct = 50 + (pos / WIN) * 45; // 5%..95%

  return (
    <div className="solo-game" data-testid="solo-tugofwar">
      <div className="solo-head">
        <div className="solo-title"><span style={{ fontSize: 17 }}>🪢</span><span>Tug of War</span></div>
        <div className="solo-score">
          {wins > 0 && <span className="pill yellow"><Trophy size={12} /> {wins} win{wins === 1 ? '' : 's'}</span>}
        </div>
      </div>

      <div className="tug-arena" data-testid="tug-arena">
        <span className="tug-side tug-you">YOU</span>
        <div className="tug-rope">
          <div className="tug-center" />
          <div className="tug-knot" style={{ left: `${markerPct}%` }} data-testid="tug-knot" />
        </div>
        <span className="tug-side tug-bot">BOT</span>
      </div>

      {phase === 'playing' ? (
        <button className="btn steprace-tap" onPointerDown={(e) => { e.preventDefault(); pull(); }} data-testid="tug-pull">PULL! 💪</button>
      ) : (
        <button className="btn" onClick={begin} data-testid="tug-start">
          {phase === 'idle' ? 'Start match' : <><RotateCcw size={15} /> Rematch</>}
        </button>
      )}

      {phase === 'done' && (
        <div className={`solo-status ${result === 'win' ? 'win' : 'lose'}`} data-testid="tug-result">
          {result === 'win' ? 'You dragged the bot over the line! 🏆' : 'The bot out-pulled you — rematch!'}
        </div>
      )}
      {phase === 'idle' && <p className="steprace-hint">Tap PULL as fast as you can to drag the knot to your side before the bot wins. It gets stronger the longer you take!</p>}
    </div>
  );
}
